import { Mail, CalendarClock } from "lucide-react";
import { CONTACT_EMAIL } from "@/data/content";
import { MaskedLines, Reveal } from "@/components/motion";
import { ContactForm } from "@/components/ContactForm";
import { usePageMeta } from "@/lib/seo";

export default function Contact() {
  usePageMeta(
    "Contact — Iconicho, LLC",
    "Connect with Iconicho, LLC for business inquiries, government contracting opportunities, partnerships, procurement, supply, logistics, training, and marketing needs."
  );
  return (
    <section className="grid-lines-light pt-36 lg:pt-44" data-testid="contact-page">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
        <Reveal>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs font-medium uppercase tracking-[0.3em] text-charcoal/60">
              Contact
            </span>
          </div>
        </Reveal>
        <MaskedLines
          testId="contact-headline"
          className="mt-8 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
          delay={0.15}
          lines={[
            "Let&rsquo;s Talk".split("&rsquo;").join("\u2019"),
            <span key="l2" className="text-gold">Business.</span>,
          ]}
        />
        <Reveal delay={0.45}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-charcoal/75">
            Have a business inquiry, contracting opportunity, partnership
            opportunity, or procurement need? Connect with Iconicho.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal delay={0.15}>
              <div className="border-t border-charcoal/15 pt-6">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  <h2 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-navy">
                    General Inquiries
                  </h2>
                </div>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="contact-email-link"
                  className="mt-4 inline-block border-b border-gold/50 pb-0.5 text-sm font-medium text-navy transition-colors duration-300 hover:border-gold"
                >
                  {CONTACT_EMAIL}
                </a>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/60">
                  Send the form and your inquiry goes directly to our team. A
                  confirmation copy is sent to your inbox.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-12 border-t border-charcoal/15 pt-6">
                <div className="flex items-center gap-3">
                  <CalendarClock className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  <h2 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-navy">
                    Prefer to Schedule?
                  </h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-charcoal/60">
                  A direct booking link is being finalized. In the meantime,
                  submit an inquiry and we will coordinate a conversation.
                </p>
                <button
                  type="button"
                  disabled
                  data-testid="schedule-conversation-button"
                  className="mt-6 cursor-not-allowed rounded-sm border border-charcoal/20 px-6 py-3.5 text-sm font-medium tracking-wide text-charcoal/50"
                >
                  Schedule a Conversation — Coming Soon
                </button>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.2}>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
